/**
 * DriverCostEditor — the company's driver cost per hour and average speed (Phase 28 / task
 * 28.5, DEC-331 D-4), beside the fuel cost so all running costs are edited in one place.
 *
 * Reads / saves GET / PATCH /v1/tenants/driver-cost-settings (MANAGE_TENANT). Disposal
 * suggestions cost a trip as fuel (miles × fuel rate) + drive time (miles ÷ speed × this
 * cost) + the disposal price. Suggestions cannot be switched on until both costs are set;
 * clearing one later makes them suggest nothing. The page says so.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useApiOpts, authedFetch } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';

import { driverCostToInput, parseAvgSpeed, parseDriverCost } from './driver-cost';

interface DriverCostSettings {
  readonly driverCostCentsPerHour: number | null;
  readonly avgSpeedMph: number;
}

const help: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };
const label: React.CSSProperties = { display: 'block', fontWeight: 600, marginBottom: '6px' };

export function DriverCostEditor(): React.JSX.Element {
  const opts = useApiOpts();
  const [saved, setSaved] = React.useState<DriverCostSettings | null>(null);
  const [costText, setCostText] = React.useState('');
  const [speedText, setSpeedText] = React.useState('');
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const apply = React.useCallback((s: DriverCostSettings): void => {
    setSaved(s);
    setCostText(driverCostToInput(s.driverCostCentsPerHour));
    setSpeedText(String(s.avgSpeedMph));
  }, []);

  const load = React.useCallback(async (): Promise<void> => {
    try {
      const res = await authedFetch(opts, '/v1/tenants/driver-cost-settings');
      if (!res.ok) {
        setLoadError(`Could not load the driver cost (${String(res.status)}).`);
        return;
      }
      const json = (await res.json()) as { settings: DriverCostSettings };
      apply(json.settings);
      setLoadError(null);
    } catch {
      setLoadError('Network error — please try again.');
    }
  }, [opts, apply]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loadError !== null) {
    return (
      <div style={{ color: 'var(--color-danger)' }}>
        <p style={{ marginBottom: '8px' }}>{loadError}</p>
        <Button variant="outline" onClick={(): void => void load()}>
          Retry
        </Button>
      </div>
    );
  }
  if (saved === null) {
    return (
      <p role="status" style={help}>
        Loading…
      </p>
    );
  }

  const cost = parseDriverCost(costText);
  const speed = parseAvgSpeed(speedText);
  const valid = cost.ok && speed.ok;
  const unchanged =
    cost.ok &&
    speed.ok &&
    cost.cents === saved.driverCostCentsPerHour &&
    speed.mph === saved.avgSpeedMph;

  const save = async (): Promise<void> => {
    if (!cost.ok || !speed.ok) return;
    // Send only what changed (the API audits each change).
    const body: { driverCostCentsPerHour?: number | null; avgSpeedMph?: number } = {};
    if (cost.cents !== saved.driverCostCentsPerHour) body.driverCostCentsPerHour = cost.cents;
    if (speed.mph !== saved.avgSpeedMph) body.avgSpeedMph = speed.mph;
    setSaving(true);
    try {
      const res = await authedFetch(opts, '/v1/tenants/driver-cost-settings', {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        toast.error(`Save failed (${String(res.status)}).`);
        return;
      }
      const json = (await res.json()) as { settings: DriverCostSettings };
      apply(json.settings);
      toast.success('Driver cost saved');
    } catch {
      toast.error('Network error — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: '520px' }}>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <label htmlFor="driver-cost" style={label}>
            Driver cost per hour ($)
          </label>
          <Input
            id="driver-cost"
            inputMode="decimal"
            placeholder="Not set"
            value={costText}
            aria-invalid={!cost.ok}
            onChange={(e): void => setCostText(e.target.value)}
            style={{ maxWidth: '160px' }}
          />
        </div>
        <div>
          <label htmlFor="avg-speed" style={label}>
            Average speed (mph)
          </label>
          <Input
            id="avg-speed"
            inputMode="numeric"
            value={speedText}
            aria-invalid={!speed.ok}
            onChange={(e): void => setSpeedText(e.target.value)}
            style={{ maxWidth: '100px' }}
          />
        </div>
      </div>
      <div style={{ marginTop: '8px' }}>
        <Button
          type="button"
          disabled={!valid || unchanged || saving}
          onClick={(): void => void save()}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
      <p role={valid ? undefined : 'alert'} style={{ ...help, marginTop: '6px' }}>
        {!cost.ok
          ? cost.message
          : !speed.ok
            ? speed.message
            : 'Used by disposal suggestions to price drive time: miles ÷ average speed × this cost. Suggestions stay off until both fuel and driver cost are set.'}
      </p>
    </div>
  );
}
